export default function RosevilleTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Roseville Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Roseville travelers actually move helps us anticipate the passport urgencies that arise here. From healthcare conferences to heritage family visits and corporate corridor travel, north metro travel operates on a unique rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* MSP Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MSP — Minneapolis-Saint Paul International Airport</h3>
                <p className="text-gray-400 text-xs">About 12 miles from Roseville — 15–20 minutes by car</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Roseville is centrally located in the metro with easy access to MSP via I-35E and I-35W. With nonstop service to Amsterdam, London, Paris, Tokyo, and Reykjavik, many residents connect directly to Europe and Asia. The airport's central location means a Roseville resident can check their passport status on Monday morning and be on a transatlantic flight by Thursday evening.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Because MSP is so close, many Roseville travelers book international flights with minimal lead time. Check your passport expiration before every travel planning conversation, not after tickets are purchased.
              </p>
            </div>
          </div>

          {/* Healthcare & Professional Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-hospital-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Healthcare &amp; Professional Travel</h3>
                <p className="text-gray-400 text-xs">St. Paul area hospitals and north metro professional services</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Roseville is home to many healthcare professionals, attorneys, and accountants who travel internationally for conferences, client meetings, and professional development. These professionals often receive travel invitations with 2–3 weeks notice — a medical conference in Geneva, a client meeting in London, a partnership summit in Singapore.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Healthcare professionals should maintain passport validity at all times. International conferences and partnerships are common, and invitations arrive with short notice.
              </p>
            </div>
          </div>

          {/* Hmong & Southeast Asian Community */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-earth-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Hmong &amp; Southeast Asian Community</h3>
                <p className="text-gray-400 text-xs">Strong international family connections requiring timely passports</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Roseville has a significant Hmong and Vietnamese American community with strong family connections to Southeast Asia. These community members travel frequently for family visits, cultural events, and business. Passport needs arise often and sometimes urgently — a family emergency in Vietnam, a cultural celebration in Laos, a wedding in Thailand.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Heritage travelers should check passport validity twice a year. Family emergencies require immediate travel capability, and standard processing cannot accommodate these timelines.
              </p>
            </div>
          </div>

          {/* University & College Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">University &amp; College Travel</h3>
                <p className="text-gray-400 text-xs">Students and academics from nearby Twin Cities campuses</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Roseville is adjacent to several higher education institutions in the Twin Cities metro. Students studying abroad, faculty attending international conferences, and researchers with international collaborations are all part of the Roseville community. Study abroad programs often have passport submission deadlines months in advance.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Study abroad applications require passport submission months in advance. Start early — and remember that children's passports expire in 5 years, not 10.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-building-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Corporate Event &amp; Conference Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Roseville's professional community hosts and attends major industry conferences and events that draw international attendees. Event organizers, keynote speakers, and corporate attendees flying in from abroad — or Roseville professionals traveling to partner events overseas — sometimes discover passport issues during event planning. We accommodate urgent corporate timelines with same-day document review and prioritized submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}